import { useEffect, useState, useContext, useRef } from "react";
import {
  Context,
  urlToPayload,
  getPropertiesFacets,
  getSearchResults,
  getWatermarkedImgixUrl,
} from "services";
import { useRouter } from "next/router";
import {
  AuctionPropertyListPageWrapper,
  PropertyListPageLayout,
  useLocation,
  V2PropertiesComponent,
} from "app-v2";
import { ViewProvider } from "app/components/propertiesPage/context/ViewContext";

const bankName = process.env.NEXT_PUBLIC_BANK;

const bankMatch = (bankUrl: string) => {
  const cleanName =
    bankName?.replace(/-limited$/i, "")?.replace(/[-\s]/g, "") || ""; // Removes hyphens and spaces

  const cleanFineBankName = bankUrl
    ?.toLowerCase()
    ?.startsWith(cleanName?.toLowerCase());
  return cleanFineBankName;
};

const originalAuctionBazaarHomePageServerSideProps = {
  metaContent: {
    title: "Bank Auction Properties at discounted rate | Auction Bazaar",
    description:
      "Explore and buy the latest bank auction properties with Auction Bazaar. The most transparent way to find and bid for the auction properties listed by banks and financial institutions.",
    url: process.env.NEXT_PUBLIC_WEB_URL,
    type: "website",
    image: getWatermarkedImgixUrl(
      `${process.env.NEXT_PUBLIC_IMAGIX_URL_STATIC}/6a556639-25c9-42e7-94bb-4fc1508c4190`,
    ),
    cardType: "summary_large_image",
    keywords: "Bank auction properties, e auction portal, auction property",
  },
  allBanners: [],
};

const banksHomePropertiesListingPageServerSideProps = {
  metaContent: {
    title:
      "Residential, Commercial, Agricultural Bank Auction properties at sale | Auction Bazaar",
    description:
      "Get Residential, Commercial, and Agricultural Bank Auction Properties at a discounted rate compared to the market rate with Auction Bazaar.",
    url: process.env.NEXT_PUBLIC_WEB_URL,
    type: "website",
    image: `${process.env.NEXT_PUBLIC_IMAGIX_URL_STATIC}/5c212b15-6a09-4b15-9d70-b4a4d47af99b`,
    cardType: "summary_large_image",
    keywords: "online property auctions, bank properties for sale",
  },
  schema: {
    __html: JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "Service",
      serviceType:
        " E-Auction properties, Bank auction properties for sale, Residential Bank auction properties for sale, Commercial Bank auction properties for sale , Movable assets Bank auction properties for sale, Industrial Bank auction properties for sale , Agricultural Bank auction properties for sale ,Auction properties for sale ",
      provider: {
        "@type": "LocalBusiness",
        name: "Auctionbazaar",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Auctionbazaar,Hyderabad,Telangana",
          addressLocality: "Hyderabad",
          addressRegion: "Telangana",
          postalCode: "500001",
          addressCountry: "India",
        },
        hasMap: "https://goo.gl/maps/jDgwk4kNuGgzBfpaA",
        openingHours: "Mo, Tu, We, Th, Fr 09:30-18:30",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+918370969696",
        },
        telephone: "+918370969696",
        priceRange: "Custom",
        image: "https://www.auctionbazaar.com/propertyOne.png",
      },

      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "E-Auction properties",
        itemListElement: [
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Bank auction properties for sale",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Residential Bank auction properties for sale",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Commercial Bank auction properties for sale",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Movable assets Bank auction properties for sale",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Industrial Bank auction properties for sale",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Agricultural Bank auction properties for sale",
            },
          },
          {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Auction properties for sale",
            },
          },
        ],
      },
    }),
  },
};

export async function getServerSideProps({ req }: any) {
  try {
    const bankUrl = req.headers["x-forwarded-host"]
      ? req.headers["x-forwarded-host"]
      : req.headers["host"];

    const isBankUrl = bankMatch(bankUrl);

    return {
      props: isBankUrl
        ? {
            urlData: {
              bank: bankName,
            },
            bankUrl: bankUrl,
            ...banksHomePropertiesListingPageServerSideProps,
          }
        : originalAuctionBazaarHomePageServerSideProps,
    };
  } catch (error) {
    const bankUrl = req.headers["x-forwarded-host"]
      ? req.headers["x-forwarded-host"]
      : req.headers["host"];

    const isBankUrl = bankMatch(bankUrl);

    return {
      props: isBankUrl
        ? {
            urlData: {
              bank: bankName,
            },
            bankUrl: bankUrl,
            ...banksHomePropertiesListingPageServerSideProps,
          }
        : originalAuctionBazaarHomePageServerSideProps,
    };
  }
}

export default function Properties({ urlData, bankUrl }: any) {
  const router = useRouter();
  const { searchForm }: any = useContext(Context);
  const [urlDataToRunOnLoad, setUrlDataToRunOnLoad] = useState<any>(urlData);
  const [cities, setCities] = useState<any>();
  const [defaultLocation, setDefaultLocation] = useState<any>();
  const [isUrlToPayloadPrepared, setIsUrlToPayloadPrepared] = useState(false);
  const urlToPayloadInitiatedRef = useRef(false);

  const { city } = useLocation(defaultLocation, cities);

  const handleCities = async () => {
    let searches: any = {
      q: "*",
      collection: "properties",
      facet: "dump.location.city_name",
      facet_by: "dump.location.city_name",
      facet_return_parent: "dump.location.city_name",
      max_facet_values: "5000",
      per_page: 0,
    };

    if (bankName) {
      searches = {
        ...searches,
        filter: "true",
        filter_by: `dump.bank.bank_slug:${bankName}`,
      };
    }
    const location: any = await getSearchResults({
      searches: [searches],
    });

    const counts = location?.results?.[0]?.facet_counts?.[0]?.counts ?? [];

    const citiesData = Array.isArray(counts)
      ? counts.map((item: any) => ({
          label: item?.parent?.city_name,
          value: item?.parent?.city_slug,
        }))
      : [];

    const allCities = [
      { label: "All Cities", value: "" },
      ...(citiesData || []),
    ];

    setCities(allCities);
    setDefaultLocation(
      location?.results?.[0]?.facet_counts?.[0]?.counts?.[0]?.parent ?? null,
    );
    if (!urlDataToRunOnLoad?.city) {
      searchForm.setField("city", { label: "All Cities", value: "" });
    }
  };

  useEffect(() => {
    (async () => {
      await handleCities();
    })();
  }, []);

  useEffect(() => {
    if (
      urlDataToRunOnLoad &&
      Object.keys(urlDataToRunOnLoad).length > 0 &&
      !urlToPayloadInitiatedRef.current
    ) {
      urlToPayloadInitiatedRef.current = true;
      (async () => {
        const dataWithoutFilter: any = await getPropertiesFacets([
          {
            attribute: "status",
            op: "eq",
            value: "active",
          },
        ]);
        urlToPayload(
          urlDataToRunOnLoad,
          searchForm,
          dataWithoutFilter,
          router,
          city,
        );
        setUrlDataToRunOnLoad({});
        setIsUrlToPayloadPrepared(true);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, city?.value]);

  return (
    <BankListingPageHomeUi
      cities={cities}
      defaultLocation={defaultLocation}
      isUrlToPayloadPrepared={isUrlToPayloadPrepared}
    />
  );
}

const BankListingPageHomeUi = ({
  cities,
  defaultLocation,
  isUrlToPayloadPrepared,
}: {
  cities: any;
  defaultLocation: any;
  isUrlToPayloadPrepared: any;
}) => {
  return (
    <>
      {process.env.NEXT_PUBLIC_PROPERTY_LIST_REDESIGN === "true" ? (
        <>
          <AuctionPropertyListPageWrapper
            cities={cities}
            defaultLocation={defaultLocation}
            isUrlToPayloadPrepared={isUrlToPayloadPrepared}
          />
        </>
      ) : (
        <PropertyListPageLayout cities={cities}>
          <ViewProvider>
            <V2PropertiesComponent
              cities={cities}
              defaultLocation={defaultLocation}
            />
          </ViewProvider>
        </PropertyListPageLayout>
      )}
    </>
  );
};
